import { useEffect, useMemo, useRef, useState } from 'react';
import { ChatMessageAiAnswerSource, isLegacyUploadFileUrl } from '@worknet/models';

import useChatStore from './useChatStore';

// The server signs citation links for 15 minutes; refresh at half that so a chat left
// open in a tab never holds a URL that has already gone stale.
const REFRESH_INTERVAL_MS = 7 * 60 * 1000;

type ResolvedUrls = {
  /** The chat and message these URLs belong to. */
  key: string;
  bySourceId: Record<string, string>;
};

/**
 * Citation sources as they should actually be rendered.
 *
 * Uploaded files are private (#2758), so they carry a `fileRef` rather than a URL and
 * have to be resolved through the conversation that cited them. Answers persisted before
 * that change still hold the old unauthenticated URL, which the server already strips;
 * it is dropped here too, so an older cached message can never render one.
 */
export function useResolvedSources(
  chatId: string | undefined,
  messageId: string,
  sources: ChatMessageAiAnswerSource[]
): ChatMessageAiAnswerSource[] {
  // Selected rather than destructured off the whole store: this runs once per rendered
  // message, so subscribing each of them to every store change would be costly.
  const getSourceFile = useChatStore((state) => state.getSourceFile);
  const [resolved, setResolved] = useState<ResolvedUrls | undefined>();

  const sanitized = useMemo(
    () =>
      sources.map((source) =>
        isLegacyUploadFileUrl(source.url) ? { ...source, url: '' } : source
      ),
    [sources]
  );

  // Which sources to ask the server to resolve, and under what id.
  //
  // A `fileRef` is the exact case. A source with no url at all is the other: answers
  // from the external AI layer carry neither a `fileRef` nor an `id`, so an uploaded
  // file is indistinguishable from any other linkless source here. The server decides —
  // it refuses anything that is not a cited upload, which leaves the source as it was.
  //
  // A string, not an array: `sources` is rebuilt every render, but the ids it holds
  // rarely change, and this is what the effect below keys off.
  const refIdsKey = sanitized
    .map((source, index) => ({ source, sourceId: String(source.id ?? index) }))
    .filter(({ source }) => !!source.fileRef || !source.url)
    .map(({ sourceId }) => sourceId)
    .join('|');

  // Messages are rendered from a list keyed by index, so this component is reused across
  // chats. Source ids are small integers that collide, so URLs must never be read unless
  // they were resolved for exactly this chat and message.
  const key = `${chatId ?? ''}::${messageId}`;
  const resolvedRef = useRef<ResolvedUrls | undefined>(undefined);
  resolvedRef.current = resolved;

  useEffect(() => {
    if (!chatId || !refIdsKey) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const previous = resolvedRef.current?.key === key ? resolvedRef.current.bySourceId : {};
      const bySourceId: Record<string, string> = {};

      await Promise.all(
        refIdsKey.split('|').map(async (sourceId) => {
          try {
            const file = await getSourceFile(chatId, messageId, sourceId);
            bySourceId[sourceId] = file.url ?? '';
          } catch {
            // Not necessarily a failure: the server refuses anything that is not a cited
            // upload, which is the expected answer for most linkless sources. And a
            // transient error must not break a link that is still valid, so keep the
            // previous one — it has minutes left on it.
            bySourceId[sourceId] = previous[sourceId] ?? '';
          }
        })
      );

      if (!cancelled) {
        setResolved({ key, bySourceId });
      }
    };

    resolve();

    // Only refresh while the tab is actually being looked at. Each refresh costs the
    // server a chat lookup, a message lookup and a signing call, per message on screen.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        resolve();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [chatId, messageId, key, refIdsKey, getSourceFile]);

  return useMemo(() => {
    const bySourceId = resolved?.key === key ? resolved.bySourceId : undefined;
    if (!bySourceId) {
      return sanitized;
    }
    return sanitized.map((source, index) => {
      const url = bySourceId[String(source.id ?? index)];
      // Only ever add a link. A source that did not resolve keeps what it had, so a
      // refused or failed lookup cannot strip a url that was already good.
      return url ? { ...source, url } : source;
    });
  }, [sanitized, resolved, key]);
}
